import { Box, SxProps, Typography } from "@mui/material";
import { BlocksContent, BlocksRenderer } from "@strapi/blocks-react-renderer";
import { RootNode } from "@strapi/blocks-react-renderer/dist/BlocksRenderer";
import { Link as GatsbyLink } from 'gatsby';
import isAbsoluteUrl from "is-absolute-url";
import React, { useMemo } from "react";
import { ErrorBoundary } from "react-error-boundary";
import {Link} from "../../../lib/mui/components/link";

const maxHeightStyles = (maxHeight?: string, maxLines?: number): SxProps => (
    {
        display: '-webkit-box',       // create a WebKit flex-box
        WebkitBoxOrient: 'vertical',  // stack children vertically
        WebkitLineClamp: maxLines,       // how many lines before clipping
        overflow: 'hidden',
        textOverflow: 'ellipsis',     // only effective once the box is clamped
        whiteSpace: 'normal',         // allow wrapping inside the clamped box
        maxHeight
    }
)

export const blockHasContent = (content: RootNode) => {
    // console.log('heree blockHasContent', content)
    if (!content || !content.children || content.children.length === 0) return false;

    return Boolean(content.children.find(e => {
        if (e.type === "text" && !e.text) return false

        return true;
    }))
}

export const blocksHasContent = (content: BlocksContent) => {
    // console.log('heree blocksHasContent', content)
    if (!content || !content.length) return false;

    if (Array.isArray(content)) {
        return Boolean(content.find(e => {
            if (blockHasContent(e)) return true;

            return false;
        }))
    }
    else {
        return blockHasContent(content);
    }
}

export const normalizeBlock = (node: any): RootNode | null => {
    if (!node) return null;

    // Text leaf
    if (node.type === "text") {
        return { ...node, text: node.text ?? "" };
    }

    // Non-text block
    const children = Array.isArray(node.children)
        ? node.children.map(normalizeBlock).filter(Boolean)
        : [];

    return { ...node, children };
};

export const normalizeBlocks = (content: any): RootNode[] => {
    if (!content) return [];
    if (Array.isArray(content)) {
        return content.map(normalizeBlock).filter(Boolean) as RootNode[];
    }
    return [normalizeBlock(content)].filter(Boolean) as RootNode[];
};

export type BlockRendererContent = BlocksContent

export const TextBlockRenderer = ({ content, maxHeight, maxLines, noParagraphSpacing }: { content: BlockRendererContent, maxHeight?: string, maxLines?: number, noParagraphSpacing?: boolean }) => {
    // Step 1: normalize to safe array-of-blocks
    const normalized = useMemo(() => normalizeBlocks(content), [content]);

    // Step 2: filter out empties
    const filteredContent = normalized.filter(blockHasContent);

    const boxStyles = useMemo(() => {
        if (!maxHeight && !maxLines) return undefined;
        return maxHeightStyles(maxHeight, maxLines);
    }, [maxHeight, maxLines]);

    if (filteredContent.length === 0) return null;

    return <ErrorBoundary fallback={<div>Failed</div>}><Box sx={boxStyles}><BlockComponent content={filteredContent} noParagraphSpacing={noParagraphSpacing} /></Box></ErrorBoundary>
}


type BlockComponentProps = { content: BlocksContent, noParagraphSpacing?: boolean }

const BlockComponent = ({ content, noParagraphSpacing }: BlockComponentProps) => {
    return (
        <BlocksRenderer
            content={content}
            blocks={{
                paragraph: ({ children }) => (
                    <p
                        style={
                            noParagraphSpacing ? { marginBlockStart: 0, marginBlockEnd: 0 } : undefined
                        }
                    >
                        {children}
                    </p>
                ),
                quote: ({ children }) => <blockquote>{children}</blockquote>,
                // ...or point to a design system
                heading: ({ children, level, plainText }) => {
                    // console.log('hereee heading', level)
                    switch (level) {
                        case 1:
                            return <Typography variant="h3">{children || plainText}</Typography>
                        case 2:
                            return <Typography variant="h4">{children || plainText}</Typography>
                        case 3:
                            return <Typography variant="h5">{children || plainText}</Typography>
                        case 4:
                            return <Typography variant="h6">{children || plainText}</Typography>
                        case 5:
                            return <Typography variant="h6" sx={{ fontSize: '1.15rem!important' }}>{children || plainText}</Typography>
                        case 6:
                            return <Typography variant="h6" sx={{ fontSize: '1rem!important' }}>{children || plainText}</Typography>
                        default:
                            return <Typography variant="body1">{children || plainText}</Typography>
                    }
                },
                // For links, you may want to use the component from your router or framework
                link: (props) => {
                    const { children, url } = props;
                    // Skip rendering an <a> when no URL is provided — empty hrefs fail crawlability/axe.
                    if (!url) {
                        return <>{children}</>
                    }
                    return (
                        <Link
                            relativeLinkComponent={!isAbsoluteUrl(url) ? GatsbyLink : "a"}
                            url={url}
                            forceLink
                            style={{ textDecoration: 'underline' }}
                        >
                            {children}
                        </Link>
                    )
                },
                code: ({ children, plainText }) => <code>{children || plainText}</code>,
                image: ({ children, image }) => image ? <img src={image.url} alt={image.alternativeText || ''} /> : null,
                list: ({ children, format, plainText }) => {
                    switch (format) {
                        case 'ordered':
                            return <ol>{children || plainText}</ol>
                        case 'unordered':
                        default:
                            return <ul>{children || plainText}</ul>
                    }
                },
            }}
            modifiers={{
                bold: ({ children }) => <strong>{children}</strong>,
                italic: ({ children }) => <span className="italic">{children}</span>,
                underline: ({ children }) => <span className="underline">{children}</span>,
                strikethrough: ({ children }) => <span className="strikethrough">{children}</span>,
                code: ({ children }) => <code>{children}</code>,
            }}
        />
    )
}